import React, { useState } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import { agentsData } from "Stores/agentsData";
import AgentSelect from "./AgentSelect";
import { DEFAULT_DAYS, defaultTimezone } from "./DayOfWeekField";
import { SetupPage, When, Step, Field, Row, Schedule, SequenceField, NotifyStep, SkipLine, openInBuilder, clampInt } from "./SetupFlow";
import { FALLBACK_INDUSTRIES, EMPLOYEE_RANGE_IDS } from "../DeepResearch/components/prospectFilterVocab";

const INDUSTRY_OPTIONS = FALLBACK_INDUSTRIES.map(x => ({ value: x, label: x }));
const EMPLOYEE_OPTIONS = EMPLOYEE_RANGE_IDS.map(x => ({ value: x, label: x }));
const MAX_SEEDS = 5;

// Count non-blank, comma-separated seeds so we can warn past the provider's 5-seed match cap.
const seedCount = raw => raw.split(",").map(s => s.trim()).filter(Boolean).length;

const CreateLookalikeProspector = observer(() => {
  const history = useHistory();
  const [name, setName] = useState("");
  const [seeds, setSeeds] = useState("");
  const [industries, setIndustries] = useState("");
  const [employeeCount, setEmployeeCount] = useState("");
  const [companyLocation, setCompanyLocation] = useState("");
  const [titles, setTitles] = useState("");
  const [perCompany, setPerCompany] = useState(5);
  const [sequenceId, setSequenceId] = useState("");
  const [sequenceName, setSequenceName] = useState("");
  const [runWhen, setRunWhen] = useState("DAILY");
  const [time, setTime] = useState("08:00");
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [dailyCap, setDailyCap] = useState(25);
  const [notifyMode, setNotifyMode] = useState("DAILY_DIGEST");
  const [saving, setSaving] = useState(false);
  const timezone = defaultTimezone();

  const nSeeds = seedCount(seeds);
  const canSubmit = nSeeds > 0 && !!sequenceId;

  const buildPayload = () => ({
    name: name.trim() || "Lookalike prospector",
    seeds: seeds.trim() || undefined,
    industries: industries.trim() || undefined,
    employeeCount: employeeCount.trim() || undefined,
    companyLocation: companyLocation.trim() || undefined,
    titles: titles.trim() || undefined,
    perCompany: clampInt(perCompany, 1, 25, 5),
    sequenceId,
    dailyCap: clampInt(dailyCap, 1, null, 25),
    notifyMode,
    timezone,
    ...(runWhen === "MANUAL" ? {} : { frequency: runWhen, time, ...(runWhen === "WEEKLY" ? { days } : {}) })
  });

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    const agent = await agentsData.createLookalikeProspector(buildPayload());
    setSaving(false);
    if (agent && agent.id) history.push(`/agents/${agent.id}`);
  };

  const customize = () => openInBuilder(history, "lookalike-prospector", buildPayload(), setSaving);

  return (
    <SetupPage
      title="Hire · Lookalike companies → people"
      name={{ value: name, onChange: setName, placeholder: "Lookalike prospector" }}
      bar={{ canSubmit, saving, onCreate: submit, onCustomize: customize, onCancel: () => history.push("/agents"), reason: "Add at least one company and pick a sequence to create it." }}>
      <When icon="clock">
        <Schedule runWhen={runWhen} setRunWhen={setRunWhen} days={days} setDays={setDays} time={time} setTime={setTime} timezone={timezone} />
      </When>
      <Step icon="building" tone="violet" title="Find companies like these">
        <Field
          hint={nSeeds > MAX_SEEDS ? `Using the first ${MAX_SEEDS} of ${nSeeds}.` : `Up to ${MAX_SEEDS}, domains or LinkedIn company URLs, comma-separated.`}
          warn={nSeeds > MAX_SEEDS}>
          <textarea className={`ag-input${nSeeds ? "" : " ag-sf__need"}`} rows={2} value={seeds} placeholder="e.g. stripe.com, plaid.com" onChange={e => setSeeds(e.target.value)} />
        </Field>
        <Row>
          <Field label="Industry">
            <AgentSelect searchable placeholder="Any industry" searchPlaceholder="Search industries…" value={industries} onChange={setIndustries} options={INDUSTRY_OPTIONS} />
          </Field>
          <Field label="Employee size">
            <AgentSelect placeholder="Any size" value={employeeCount} onChange={setEmployeeCount} options={EMPLOYEE_OPTIONS} />
          </Field>
          <Field label="Locations">
            <input className="ag-input" value={companyLocation} placeholder="Anywhere" onChange={e => setCompanyLocation(e.target.value)} />
          </Field>
        </Row>
      </Step>
      <Step icon="user" tone="violet" title="Find people there">
        <Row>
          <Field label="Job titles">
            <input className="ag-input" value={titles} placeholder="Anyone, or e.g. VP Sales, Founder" onChange={e => setTitles(e.target.value)} />
          </Field>
          <Field label="People per company">
            <input className="ag-input" type="number" min="1" max="25" value={perCompany} onChange={e => setPerCompany(e.target.value)} />
          </Field>
          <Field label="Contacts per run">
            <input className="ag-input" type="number" min="1" value={dailyCap} onChange={e => setDailyCap(e.target.value)} />
          </Field>
        </Row>
      </Step>
      <Step icon="mail" tone="violet" title="Find their emails" tags={[{ label: "For email sequences" }]} />
      <Step icon="send" tone="amber" title="Add to a sequence">
        <SequenceField id={sequenceId} name={sequenceName} onPick={s => { setSequenceId(s.id); setSequenceName(s.name); }} />
      </Step>
      <NotifyStep value={notifyMode} onChange={setNotifyMode} />
      <SkipLine />
    </SetupPage>
  );
});

export default CreateLookalikeProspector;
